import {
  Add as AddIcon,
  Close as CloseIcon,
  PersonAddAlt1 as PersonAddAlt1Icon,
  Place as PlaceIcon,
  Delete as DeleteIcon,
  Warehouse as WarehouseIcon,
} from '@mui/icons-material'
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import type { PickupLocation } from '@otw/models'
import type { Contact } from '@otw/models/leads/contacts'
import type {
  ContactCompany,
  ContactCompanyMember,
  ContactCompanyOwnerId,
  ContactCompanyRole,
} from '@otw/models/leads/contactCompanies'
import type { Terminal } from '@otw/models/leads/terminals'
import { getTerminalDisplayName } from '@otw/models/leads/terminals'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from 'contexts/AuthContext'
import {
  getUserDisplayName,
  isInventoryAssignableUser,
  isSalesAssignableUser,
  useInventoryAssignableUsers,
  useSalesAssignableUsers,
} from 'hooks/useUsers'
import {
  CreateContactCompanyInput,
  UpdateContactCompanyInput,
  useContactCompanies,
} from 'hooks/contacts/useContactCompanies'
import { useContacts } from 'hooks/contacts/useContacts'
import { getContactPrimaryCompanyName } from '../../util/contactCompanyMembership'
import { usePickupLocations } from 'hooks/inventory/usePickupLocations'
import { useTerminals } from 'hooks/terminals/useTerminals'
import AddLocationDialog from 'views/SuperAdmin/Management/PickupLocations/components/AddLocationDialog'
import PickupLocationSelector from '../PickupLocationSelector'
import TerminalFormDialog from 'views/SuperAdmin/Management/TerminalManagement/components/TerminalFormDialog'
import { resolveTerminalLocation } from '@/core/terminals/terminalAddress'
import { createTerminalFilterOptions } from '@/core/terminals/terminalSearch'
import {
  buildPickupLocationPrefillFromCompany,
  sortCompanyMemberContacts,
  type CompanyMemberRef,
} from '../../util/companyLocationPrefill'

interface ContactCompanyFormDialogProps {
  open: boolean
  onClose: () => void
  /** When provided, the dialog operates in edit mode for this company. */
  company?: ContactCompany | null
  /** When creating, pre-link this contact as a company member. */
  defaultMemberContact?: Contact | null
  defaultMemberRole?: ContactCompanyRole
}

const formatDigitsAsUsPhone = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 10)
  if (digits.length <= 3) return digits
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

const repSelectValue = (id: ContactCompanyOwnerId): string =>
  id === null || id === undefined ? '' : String(id)

const parseRepSelect = (v: string): ContactCompanyOwnerId => {
  if (v === '') return null
  const n = Number(v)
  return Number.isFinite(n) && String(n) === v ? n : v
}

const ROLE_OPTIONS: { value: ContactCompanyRole; label: string }[] = [
  { value: 'owner', label: 'Owner' },
  { value: 'manager', label: 'Manager' },
  { value: 'partner', label: 'Partner' },
]

interface PendingCompanyMember {
  contactId: string
  contactName: string
  role: ContactCompanyRole
}

const toDropoffRows = (locationIds: string[] | undefined): Array<string | null> =>
  locationIds?.length ? locationIds.map((id) => id) : [null]

const commitDropoffRows = (rows: Array<string | null>): string[] => {
  const seen = new Set<string>()
  const out: string[] = []
  for (const id of rows) {
    const trimmed = id?.trim()
    if (!trimmed || seen.has(trimmed)) continue
    seen.add(trimmed)
    out.push(trimmed)
  }
  return out
}

const ContactCompanyFormDialog: React.FC<ContactCompanyFormDialogProps> = ({
  open,
  onClose,
  company,
  defaultMemberContact = null,
  defaultMemberRole = 'owner',
}) => {
  const { userInfo } = useAuth()
  const { createCompany, updateCompany, isSaving, companies: contactCompanies } = useContactCompanies()
  const { terminals } = useTerminals()
  const { pickupLocations, createPickupLocation } = usePickupLocations()
  const { users: salesAssignableOwners, isLoading: isLoadingSalesOwners } =
    useSalesAssignableUsers()
  const { users: inventoryAssignableOwners, isLoading: isLoadingInventoryOwners } =
    useInventoryAssignableUsers()
  const { contacts } = useContacts()
  const appliedDefaultOwnerRef = useRef(false)
  const prevOpenRef = useRef(false)

  const assignableOwners = useMemo(() => {
    const seen = new Set<string>()
    const list: typeof salesAssignableOwners = []
    for (const user of [...salesAssignableOwners, ...inventoryAssignableOwners]) {
      const id = String(user.id)
      if (seen.has(id)) continue
      seen.add(id)
      list.push(user)
    }
    return list.sort((a, b) => getUserDisplayName(a).localeCompare(getUserDisplayName(b)))
  }, [salesAssignableOwners, inventoryAssignableOwners])
  const isLoadingOwners = isLoadingSalesOwners || isLoadingInventoryOwners

  const [name, setName] = useState('')
  const [website, setWebsite] = useState('')
  const [notes, setNotes] = useState('')
  const [ownerId, setOwnerId] = useState<ContactCompanyOwnerId>(null)
  const [businessAddressId, setBusinessAddressId] = useState<string | null>(null)
  const [dropoffRows, setDropoffRows] = useState<Array<string | null>>([null])
  const [selectedTerminals, setSelectedTerminals] = useState<Terminal[]>([])
  const [terminalCreateOpen, setTerminalCreateOpen] = useState(false)
  const [dropoffCreateDialogOpen, setDropoffCreateDialogOpen] = useState(false)
  const [addContactValue, setAddContactValue] = useState<Contact | null>(null)
  const [addContactRole, setAddContactRole] = useState<ContactCompanyRole>('manager')
  const [pendingMembers, setPendingMembers] = useState<PendingCompanyMember[]>([])

  const addableContacts = useMemo(() => {
    const memberIds = new Set(pendingMembers.map((m) => m.contactId))
    return contacts.filter((c) => !memberIds.has(c.id))
  }, [contacts, pendingMembers])

  const companyMemberContacts = useMemo(() => {
    const memberRefs: CompanyMemberRef[] = company
      ? company.members.map((m) => ({ contactId: m.contactId, role: m.role }))
      : pendingMembers.map((m) => ({ contactId: m.contactId, role: m.role }))
    return sortCompanyMemberContacts(memberRefs, contacts)
  }, [company, pendingMembers, contacts])

  const dropoffLocationPrefill = useMemo(
    () =>
      buildPickupLocationPrefillFromCompany(name, companyMemberContacts) ??
      (name.trim() ? ({ company_name: name.trim() } as PickupLocation) : null),
    [name, companyMemberContacts],
  )

  useEffect(() => {
    const justOpened = open && !prevOpenRef.current
    prevOpenRef.current = open
    if (!justOpened) {
      if (!open) {
        appliedDefaultOwnerRef.current = false
      }
      return
    }

    if (company) {
      setName(company.name ?? '')
      setWebsite(company.website ?? '')
      setNotes(company.notes ?? '')
      setOwnerId(company.ownerId ?? null)
      setBusinessAddressId(company.businessAddressId ?? null)
      setDropoffRows(toDropoffRows(company.dropoffLocationIds))
      setSelectedTerminals(
        terminals.filter((t) => (company.terminalIds ?? []).includes(t.id)),
      )
      setAddContactValue(null)
      setAddContactRole('manager')
      setPendingMembers([])
    } else {
      setName('')
      setWebsite('')
      setNotes('')
      setOwnerId(null)
      setBusinessAddressId(null)
      setDropoffRows([null])
      setSelectedTerminals([])
      setAddContactValue(null)
      setAddContactRole('manager')
      if (defaultMemberContact) {
        setPendingMembers([
          {
            contactId: defaultMemberContact.id,
            contactName: defaultMemberContact.name?.trim() || 'Unnamed contact',
            role: defaultMemberRole,
          },
        ])
      } else {
        setPendingMembers([])
      }
    }
  }, [open, company, defaultMemberContact, defaultMemberRole, terminals])

  // Replace any stub terminal selections once the terminals query catches up.
  useEffect(() => {
    if (!open || selectedTerminals.length === 0) return
    setSelectedTerminals((prev) => {
      let changed = false
      const next = prev.map((selected) => {
        const fresh = terminals.find((t) => t.id === selected.id)
        if (fresh && fresh !== selected) {
          changed = true
          return fresh
        }
        return selected
      })
      return changed ? next : prev
    })
  }, [open, terminals, selectedTerminals.length])

  useEffect(() => {
    if (!open || company || appliedDefaultOwnerRef.current || isLoadingOwners) return

    const currentUserId = userInfo?.id
    if (
      !currentUserId ||
      !(isSalesAssignableUser(userInfo) || isInventoryAssignableUser(userInfo)) ||
      !assignableOwners.some((user) => String(user.id) === String(currentUserId))
    ) {
      return
    }

    setOwnerId(currentUserId)
    appliedDefaultOwnerRef.current = true
  }, [open, company, userInfo, assignableOwners, isLoadingOwners])

  const handleAddPendingMember = () => {
    if (!addContactValue) return
    const contactName = addContactValue.name?.trim() || 'Unnamed contact'
    setPendingMembers((prev) => [
      ...prev,
      { contactId: addContactValue.id, contactName, role: addContactRole },
    ])
    setAddContactValue(null)
    setAddContactRole('manager')
  }

  const handleRemovePendingMember = (contactId: string) => {
    setPendingMembers((prev) => prev.filter((m) => m.contactId !== contactId))
  }

  const handlePendingMemberRoleChange = (contactId: string, role: ContactCompanyRole) => {
    setPendingMembers((prev) =>
      prev.map((m) => (m.contactId === contactId ? { ...m, role } : m)),
    )
  }

  const handleDropoffRowChange = (index: number, locationId: string | null) => {
    setDropoffRows((prev) => prev.map((rowId, rowIndex) => (rowIndex === index ? locationId : rowId)))
  }

  const handleAddDropoffRow = () => {
    setDropoffRows((prev) => [...prev, null])
  }

  const handleRemoveDropoffRow = (index: number) => {
    setDropoffRows((prev) => {
      const next = prev.filter((_, rowIndex) => rowIndex !== index)
      return next.length > 0 ? next : [null]
    })
  }

  const handleDropoffLocationCreated = (locationId: string | null | undefined) => {
    if (!locationId?.trim()) return
    setDropoffRows((prev) => {
      const emptyIndex = prev.findIndex((rowId) => !rowId?.trim())
      const targetIndex = emptyIndex >= 0 ? emptyIndex : prev.length - 1
      return prev.map((rowId, rowIndex) => (rowIndex === targetIndex ? locationId : rowId))
    })
    setDropoffCreateDialogOpen(false)
  }

  const valid = name.trim().length > 0

  const dialogTitle = company ? 'Edit company' : 'Add company'
  const submitLabel = useMemo(() => {
    if (isSaving) return 'Saving…'
    return company ? 'Save' : 'Create'
  }, [isSaving, company])

  const handleSubmit = async () => {
    const dropoffLocationIds = commitDropoffRows(dropoffRows)
    const terminalIds = selectedTerminals.map((t) => t.id)
    if (company) {
      const input: UpdateContactCompanyInput = {
        id: company.id,
        name: name.trim(),
        website: website.trim(),
        notes: notes.trim(),
        ownerId,
        businessAddressId,
        dropoffLocationIds,
        terminalIds,
      }
      await updateCompany(input)
    } else {
      const now = new Date()
      const members: ContactCompanyMember[] = pendingMembers.map((m) => ({
        contactId: m.contactId,
        role: m.role,
        addedAt: now,
      }))
      const input: CreateContactCompanyInput = {
        name: name.trim(),
        website: website.trim(),
        notes: notes.trim(),
        ownerId,
        ...(businessAddressId ? { businessAddressId } : {}),
        dropoffLocationIds,
        terminalIds,
        ...(members.length > 0 ? { members } : {}),
      }
      await createCompany(input)
    }
    onClose()
  }

  return (
    <>
      <Dialog open={open} onClose={() => !isSaving && onClose()} maxWidth="md" fullWidth>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.8 }}>
                Company
              </Typography>
              <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Company name"
                    required
                    fullWidth
                    size="small"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={isSaving}
                    autoFocus
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="company-owner-label">Company owner</InputLabel>
                    <Select
                      labelId="company-owner-label"
                      label="Company owner"
                      value={repSelectValue(ownerId)}
                      onChange={(e) => setOwnerId(parseRepSelect(String(e.target.value)))}
                      disabled={isSaving || isLoadingOwners}
                    >
                      <MenuItem value="">
                        <em>Unassigned</em>
                      </MenuItem>
                      {assignableOwners.map((user) => (
                        <MenuItem key={String(user.id)} value={String(user.id)}>
                          {getUserDisplayName(user)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Website"
                    fullWidth
                    size="small"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    disabled={isSaving}
                    placeholder="https://"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Notes"
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    disabled={isSaving}
                    placeholder="Internal context for sales reps (optional)"
                  />
                </Grid>
              </Grid>
            </Box>

            {!company ? (
              <>
                <Divider />
                <Box>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ letterSpacing: 0.8, display: 'block', mb: 1 }}
                  >
                    Contacts
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="stretch" sx={{ mb: 1.5 }}>
                    <Autocomplete
                      size="small"
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        '& .MuiOutlinedInput-root': { height: '100%' },
                      }}
                      options={addableContacts}
                      value={addContactValue}
                      onChange={(_, value) => setAddContactValue(value)}
                      disabled={isSaving}
                      getOptionLabel={(option) => option.name || ''}
                      isOptionEqualToValue={(opt, val) => opt.id === val.id}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          <Stack>
                            <Typography variant="body2" fontWeight={600}>
                              {option.name || 'Unnamed contact'}
                            </Typography>
                            {(() => {
                              const primaryCompany = getContactPrimaryCompanyName(
                                option.id,
                                contactCompanies,
                              )
                              const secondary = [primaryCompany, option.email]
                                .filter(Boolean)
                                .join(' • ')
                              if (!secondary) return null
                              return (
                                <Typography variant="caption" color="text.secondary">
                                  {secondary}
                                </Typography>
                              )
                            })()}
                          </Stack>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search contacts to add"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <PersonAddAlt1Icon
                                    fontSize="small"
                                    sx={{ color: 'text.secondary' }}
                                  />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                    <TextField
                      select
                      size="small"
                      label="Role"
                      value={addContactRole}
                      onChange={(e) =>
                        setAddContactRole(e.target.value as ContactCompanyRole)
                      }
                      disabled={isSaving}
                      sx={{
                        flexShrink: 0,
                        minWidth: 120,
                        '& .MuiOutlinedInput-root': { height: '100%' },
                      }}
                    >
                      {ROLE_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <Button
                      size="small"
                      variant="contained"
                      disableElevation
                      startIcon={<AddIcon />}
                      onClick={handleAddPendingMember}
                      disabled={!addContactValue || isSaving}
                      sx={{
                        flexShrink: 0,
                        textTransform: 'none',
                        fontWeight: 600,
                        alignSelf: 'stretch',
                      }}
                    >
                      Add
                    </Button>
                  </Stack>
                  {pendingMembers.length > 0 ? (
                    <Stack spacing={1}>
                      {pendingMembers.map((member) => (
                        <Stack
                          key={member.contactId}
                          direction="row"
                          spacing={1}
                          alignItems="center"
                        >
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ flex: 1, minWidth: 0 }}
                            noWrap
                          >
                            {member.contactName}
                          </Typography>
                          <TextField
                            select
                            size="small"
                            value={member.role}
                            onChange={(e) =>
                              handlePendingMemberRoleChange(
                                member.contactId,
                                e.target.value as ContactCompanyRole,
                              )
                            }
                            disabled={isSaving}
                            sx={{ minWidth: 120, flexShrink: 0 }}
                          >
                            {ROLE_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </TextField>
                          <IconButton
                            size="small"
                            aria-label={`Remove ${member.contactName}`}
                            onClick={() => handleRemovePendingMember(member.contactId)}
                            disabled={isSaving}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      Optional — link existing contacts to this company before saving.
                    </Typography>
                  )}
                </Box>
              </>
            ) : null}

            <Divider />

            <Box>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ letterSpacing: 0.8, display: 'block', mb: 0.5 }}
              >
                Business address
              </Typography>
              {companyMemberContacts.length > 0 ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  New addresses prefill the company name and let you pick contacts from this
                  company. City and/or state alone can be saved when the street address is unknown.
                </Typography>
              ) : null}
              <PickupLocationSelector
                label="Business address (optional)"
                addressKind="business"
                value={businessAddressId}
                onChange={setBusinessAddressId}
                disabled={isSaving}
                companyName={name}
                memberContacts={companyMemberContacts}
                allowPartialAddress
                otherLocationIds={dropoffRows}
              />
            </Box>

            <Divider />

            <Box>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ letterSpacing: 0.8, display: 'block', mb: 1 }}
              >
                Terminals
              </Typography>
              <Autocomplete
                multiple
                size="small"
                options={terminals}
                value={selectedTerminals}
                onChange={(_, value) => setSelectedTerminals(value)}
                disabled={isSaving}
                filterOptions={createTerminalFilterOptions(pickupLocations)}
                getOptionLabel={(option) =>
                  getTerminalDisplayName(
                    option,
                    resolveTerminalLocation(option, pickupLocations),
                  )
                }
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      size="small"
                      icon={<WarehouseIcon />}
                      label={getTerminalDisplayName(
                        option,
                        resolveTerminalLocation(option, pickupLocations),
                      )}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search by name, number, or address"
                    helperText="Associated terminal addresses become delivery address options."
                  />
                )}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<WarehouseIcon />}
                onClick={() => setTerminalCreateOpen(true)}
                disabled={isSaving}
                sx={{ mt: 1.5, textTransform: 'none' }}
              >
                Create new terminal
              </Button>
            </Box>

            <Divider />

            <Box>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ letterSpacing: 0.8, display: 'block', mb: 0.5 }}
              >
                Dropoff address
              </Typography>
              {companyMemberContacts.length > 0 ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Same prefill and partial-address rules as the business address above.
                </Typography>
              ) : null}
              <Stack spacing={2}>
                {dropoffRows.map((rowId, index) => (
                  <Stack
                    key={`dropoff-row-${index}`}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <PickupLocationSelector
                        label={
                          index === 0
                            ? 'Dropoff address (optional)'
                            : `Dropoff address ${index + 1}`
                        }
                        addressKind="dropoff"
                        value={rowId}
                        onChange={(id) => handleDropoffRowChange(index, id)}
                        disabled={isSaving}
                        companyName={name}
                        memberContacts={companyMemberContacts}
                        allowPartialAddress
                        showCreateButton={false}
                        otherLocationIds={[
                          businessAddressId,
                          ...dropoffRows.filter((_, rowIndex) => rowIndex !== index),
                        ]}
                      />
                    </Box>
                    {index > 0 ? (
                      <IconButton
                        size="small"
                        aria-label={`Remove additional dropoff address ${index + 1}`}
                        onClick={() => handleRemoveDropoffRow(index)}
                        disabled={isSaving}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    ) : null}
                  </Stack>
                ))}
              </Stack>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                sx={{ mt: 1.5 }}
              >
                <Button
                  variant="text"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddDropoffRow}
                  disabled={isSaving}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Add another address
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PlaceIcon />}
                  onClick={() => setDropoffCreateDialogOpen(true)}
                  disabled={isSaving}
                  sx={{ textTransform: 'none' }}
                >
                  Create new dropoff address
                </Button>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleSubmit()}
            disabled={isSaving || !valid}
          >
            {submitLabel}
          </Button>
        </DialogActions>
      </Dialog>

      <AddLocationDialog
        open={dropoffCreateDialogOpen}
        onClose={() => setDropoffCreateDialogOpen(false)}
        location={dropoffLocationPrefill}
        isRepairShop={false}
        allowPartialAddress
        memberContacts={companyMemberContacts}
        requireContactInfo={!companyMemberContacts.length}
        onCreate={async (data) => {
          const created = await createPickupLocation(
            data as Omit<PickupLocation, 'id' | 'created_at' | 'updated_at'>,
          )
          handleDropoffLocationCreated(created.id)
        }}
      />

      <TerminalFormDialog
        open={terminalCreateOpen}
        onClose={() => setTerminalCreateOpen(false)}
        defaultCompanyId={company?.id ?? null}
        onCreated={(terminalId) => {
          const created = terminals.find((t) => t.id === terminalId)
          if (created) {
            setSelectedTerminals((prev) =>
              prev.some((t) => t.id === terminalId) ? prev : [...prev, created],
            )
          } else {
            // Query may not have refreshed yet — keep a stub until next open.
            setSelectedTerminals((prev) =>
              prev.some((t) => t.id === terminalId)
                ? prev
                : [
                    ...prev,
                    {
                      id: terminalId,
                      pickup_location_id: null,
                      createdAt: new Date(),
                      updatedAt: new Date(),
                      createdByUserId: '',
                    },
                  ],
            )
          }
        }}
      />
    </>
  )
}

export default ContactCompanyFormDialog
